"use client"

import { Button } from "@/shared/components/ui/button"
import { DownloadCloud } from "lucide-react"
import React, { useCallback, useState } from "react"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useFilterSearchExport } from "../context/filter-search-export-context"
import { toast } from "sonner"
import { format } from "date-fns"
import type { FilterSearchHome } from "../hooks/filter-search-homes.hook"
import {
    shouldMaskHomeownerData,
    maskHomeownerLastName,
    maskHomeownerPhone,
    maskHomeownerAddress,
} from "@/shared/lib/utils/homeowner-privacy.utils"
import { displayOverallStatusLabel } from "../lib/overall-status.utils"

function escapeCsvCell(val: string | number | null | undefined): string {
    if (val === null || val === undefined) return ""
    const s = String(val).trim()
    if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`
    return s
}

function HomeownersDownloadButton() {
    const { session } = useSession()
    const { filteredHomesForExport } = useFilterSearchExport()
    const [isLoading, setIsLoading] = useState(false)

    const canShow = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    const handleDownload = useCallback(() => {
        if (!canShow || !filteredHomesForExport || filteredHomesForExport.length === 0) {
            toast.error("No filtered homeowners to download")
            return
        }
        setIsLoading(true)
        try {
            const headers = [
                "Homeowner",
                "Phone",
                "Street Address",
                "City",
                "Overall Status",
                "Goodwill Status",
                "Updated",
            ]
            const rows = filteredHomesForExport.map((home: FilterSearchHome) => {
                const fullname = home.homeowner?.user?.fullname ?? ""
                const phone = home.homeowner?.user?.phone ?? ""
                const address = home.address ?? ""
                const city = home.city ?? ""
                const overallStatus = displayOverallStatusLabel(home.homeStatusInfo?.overallStatus) ?? ""
                const goodwillStatus = (home as any).goodwillStatus ?? "No Assistance Required"
                const updatedAt = home.updatedAt
                    ? format(new Date(home.updatedAt), "yyyy-MM-dd")
                    : ""

                const userId = home.homeowner?.user?.id
                const mask = shouldMaskHomeownerData(session?.role, session?.sub, userId)
                const displayName = mask ? maskHomeownerLastName(fullname) : fullname
                const displayPhone = mask ? maskHomeownerPhone(phone) : phone
                const displayAddress = mask ? maskHomeownerAddress(address) : address

                return [
                    escapeCsvCell(displayName),
                    escapeCsvCell(displayPhone),
                    escapeCsvCell(displayAddress),
                    escapeCsvCell(city),
                    escapeCsvCell(overallStatus),
                    escapeCsvCell(goodwillStatus),
                    escapeCsvCell(updatedAt),
                ].join(",")
            })
            const csv = [headers.join(","), ...rows].join("\n")
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
            const url = URL.createObjectURL(blob)
            const a = document.createElement("a")
            a.href = url
            a.download = `homeowners-filtered-${format(new Date(), "yyyy-MM-dd")}.csv`
            a.click()
            URL.revokeObjectURL(url)
            toast.success(
                `Downloaded ${filteredHomesForExport.length} homeowner${filteredHomesForExport.length !== 1 ? "s" : ""}`
            )
        } catch (e) {
            toast.error(e instanceof Error ? e.message : "Download failed")
        } finally {
            setIsLoading(false)
        }
    }, [canShow, filteredHomesForExport, session?.role, session?.sub])

    if (!canShow) return null

    return (
        <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-2 py-1 h-10 print-hidden"
            onClick={handleDownload}
            disabled={isLoading || !filteredHomesForExport || filteredHomesForExport.length === 0}
            showLoader={false}
        >
            <DownloadCloud className="w-5 h-5" />
            <span>Download</span>
        </Button>
    )
}

export default HomeownersDownloadButton
